/* The ⌘K search palette (mockups/crm-v3-contacts-v1.html, section #search).

   A 720px panel over a dimmed screen, one input, results grouped Contacts /
   Messages / Quotes / Jobs / Clients in that order with the empty groups
   dropped. Each row is a kind badge, a title, the field that matched with the
   match highlighted, and a right-aligned date or amount.

   All the matching lives in src/lib/search — this file only renders it and
   handles the keyboard. The two things worth knowing before editing:

   - The index is built in two pieces (core collections, then messages) and
     merged, so the lazy arrival of the interactions collection never
     re-normalizes every contact again. Measured 26 ms for the core index over
     a live-sized CRM, which is why this stays on the main thread.
   - The query is debounced 150 ms AND passed through useDeferredValue. The
     debounce keeps a fast typist from running the search on every keystroke;
     the deferred value keeps the input itself painting even when a search does
     run long. Removing either one makes typing feel sticky on a big CRM. */
import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { C, font, mono } from "../../lib/styles.js";
import {
  buildIndex, mergeIndexes, search, groupResults, snippet, highlightParts,
  KIND_BADGES,
} from "../../lib/search/index.js";
import { useAllInteractions } from "./useAllInteractions.js";

const DEBOUNCE_MS = 150;
const MESSAGES_MIN_CHARS = 3;
const PER_GROUP = 8;

export const SEARCH_EVENT = "suite:open-search";

/* ── right-hand column ─────────────────────────────────────────────────── */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* "Sep 4" for a date this year, "Sep 2025" further back. Never Intl: the
   palette renders hundreds of these and the format is fixed. */
function shortDate(iso, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const [, y, mo, d] = m;
  const month = MONTHS[Number(mo) - 1] || "";
  return Number(y) === now.getFullYear() ? `${month} ${Number(d)}` : `${month} ${y}`;
}

/* "7 mo ago", the same shape the Contacts table's last-touch column uses. */
function relativeAge(iso, now = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  if (!m) return "";
  const days = Math.floor((now - Date.parse(`${m[1]}-${m[2]}-${m[3]}T00:00:00Z`)) / 86400000);
  if (!Number.isFinite(days) || days < 0) return "today";
  if (days === 0) return "today";
  if (days < 14) return `${days} d ago`;
  if (days < 60) return `${Math.round(days / 7)} wk ago`;
  if (days < 730) return `${Math.round(days / 30)} mo ago`;
  return `${Math.round(days / 365)} yr ago`;
}

function money(n) {
  const v = typeof n === "number" ? n : Number(String(n ?? "").replace(/[$,]/g, ""));
  if (!Number.isFinite(v) || v === 0) return "";
  return `$${Math.round(v).toLocaleString("en-US")}`;
}

/* What each kind puts in the right column, per the mockup: contacts and
   clients show how long ago, a quote shows its total, a message its date. */
function rightLabel(result, now) {
  const r = result.record || {};
  switch (result.kind) {
    case "contact": return relativeAge(result.at, now);
    case "client": return r.clientCode || relativeAge(result.at, now);
    case "job": return shortDate(result.at, now);
    case "quote": return money(r.grandTotal ?? r.total ?? r.subtotal) || shortDate(result.at, now);
    default: return shortDate(result.at, now);
  }
}

/* ── one row ───────────────────────────────────────────────────────────── */

function Row({ result, selected, onHover, onOpen, now, rowRef, isMobile }) {
  const cut = snippet(result, 44);
  const parts = highlightParts(cut.text, cut.spans);
  return (
    <div
      ref={rowRef}
      role="option"
      aria-selected={selected}
      data-testid={`search-row-${result.kind}-${result.id}`}
      className="crm-search-row"
      style={{ ...S.row, ...(isMobile ? S.rowMobile : null), ...(selected ? S.rowSelected : null) }}
      onMouseMove={onHover}
      onClick={onOpen}
    >
      <span style={S.badge}>{KIND_BADGES[result.kind] || "·"}</span>
      <span style={isMobile ? { ...S.rowText, ...S.rowTextMobile } : S.rowText}>
        {result.title}
        <span style={S.rowMeta}>
          {result.subtitle ? ` · ${result.subtitle}` : ""}
          {` · ${result.matchedField} `}
          {cut.leading ? "…" : ""}
          {parts.map((p, i) => (p.hit
            ? <mark key={i} style={S.mark}>{p.text}</mark>
            : <React.Fragment key={i}>{p.text}</React.Fragment>))}
          {cut.trailing ? "…" : ""}
        </span>
      </span>
      <span style={S.rowRight}>{rightLabel(result, now)}</span>
    </div>
  );
}

/* ── the palette ───────────────────────────────────────────────────────── */

/**
 * Keep Tab inside the dialog.
 *
 * The palette is aria-modal, and until now that was a claim rather than a
 * behaviour: one Tab out of the input landed on the page's own search bar,
 * behind a dimmed overlay, still typing into a list nobody could see. There
 * are only two focusable things in here — the input and the esc button — so
 * this is a cycle of two, but it is written against whatever the panel holds
 * so a third control does not silently break it.
 */
export function trapTab(e, panel) {
  if (!panel) return;
  const nodes = [...panel.querySelectorAll(
    'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
  )].filter((el) => !el.disabled && el.tabIndex !== -1);
  if (!nodes.length) return;
  /* Tab is moved by hand rather than by letting the browser wrap at the edges,
     because the browsers disagree about the edges: Safari does not put a
     <button> in the tab order by default, so a trap that only intervenes on
     the last element would let one Tab out of the input escape the dialog
     there and not in Chrome. Driving it from the list is the same in both. */
  e.preventDefault();
  const i = nodes.indexOf(document.activeElement);
  const step = e.shiftKey ? -1 : 1;
  const next = i < 0
    ? (e.shiftKey ? nodes.length - 1 : 0)
    : (i + step + nodes.length) % nodes.length;
  nodes[next].focus();
}

/**
 * @param open           whether the palette is showing
 * @param onClose        called on Escape, backdrop click, or after opening a record
 * @param initialQuery   what the user already typed into the page's search bar
 * @param collections    { contacts, clients, jobs, savedQuotes }
 * @param role           normalized role from jobAccess(); gates the messages group
 * @param isMobile       matchMedia("(max-width: 767px)"), owned by CrmApp
 * @param onOpenContact  open the contact drawer
 * @param onOpenClient   open the client drawer
 * @param onOpenOther    a job or quote row — read-only until cross-app nav exists
 */
export default function SearchPalette({
  open,
  onClose,
  initialQuery = "",
  collections,
  role,
  isMobile,
  onOpenContact,
  onOpenClient,
  onOpenOther,
}) {
  const [query, setQuery] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const panelRef = useRef(null);
  const rowRefs = useRef([]);
  const restoreFocusTo = useRef(null);
  /* The window key handler reads these through refs so it can be bound once
     per opening. Binding it on [results, selected] meant every arrow press
     removed and re-added a window listener, and a listener that is being
     replaced while a key repeat is in flight is how a held ArrowDown skips
     rows. */
  const resultsRef = useRef([]);
  const selectedRef = useRef(0);

  const { contacts, clients, jobs, savedQuotes } = collections || {};
  const messages = useAllInteractions(role);
  const { load: loadMessages, allowed: messagesAllowed } = messages;

  /* Focus the input on open and hand focus back to wherever it was on close. */
  useEffect(() => {
    if (!open) return undefined;
    restoreFocusTo.current = document.activeElement;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      cancelAnimationFrame(id);
      const el = restoreFocusTo.current;
      if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
    };
  }, [open]);

  /* Each opening starts from what the page's search bar already holds. */
  useEffect(() => {
    if (!open) return;
    setQuery(initialQuery);
    setDebounced(initialQuery);
    setSelected(0);
  }, [open, initialQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  /* Ask for the admin-only collection the first time a query is long enough to
     be worth searching bodies for. load() is idempotent and session-cached. */
  useEffect(() => {
    if (open && messagesAllowed && debounced.trim().length >= MESSAGES_MIN_CHARS) loadMessages();
  }, [open, messagesAllowed, debounced, loadMessages]);

  /* Two indexes, merged: the core one is rebuilt only when a collection
     identity changes, and messages arriving never touch it. */
  const coreIndex = useMemo(
    () => buildIndex({ contacts, clients, jobs, quotes: savedQuotes }),
    [contacts, clients, jobs, savedQuotes],
  );
  const messageIndex = useMemo(
    () => buildIndex({ interactions: messagesAllowed ? messages.records : [] }),
    [messagesAllowed, messages.records],
  );
  const index = useMemo(() => mergeIndexes(coreIndex, messageIndex), [coreIndex, messageIndex]);

  /* useDeferredValue on top of the debounce: React keeps painting the input
     from the newest value while the (heavier) result list catches up. */
  const deferredQuery = useDeferredValue(debounced);
  const results = useMemo(
    () => (open ? search(index, deferredQuery, { limit: PER_GROUP }) : []),
    [open, index, deferredQuery],
  );
  const groups = useMemo(() => groupResults(results), [results]);
  const now = useMemo(() => new Date(), [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setSelected(0); }, [deferredQuery]);
  useEffect(() => { resultsRef.current = results; }, [results]);
  useEffect(() => { selectedRef.current = selected; }, [selected]);
  rowRefs.current = [];

  const openResult = useCallback((result) => {
    if (!result) return;
    if (result.kind === "contact") onOpenContact?.(result.record);
    else if (result.kind === "client") onOpenClient?.(result.record);
    else onOpenOther?.(result);
    onClose?.();
  }, [onOpenContact, onOpenClient, onOpenOther, onClose]);

  /* On the window, not on the panel. The palette is modal, so its keys should
     work wherever focus happens to be — and for one frame after it opens,
     focus is still on whatever opened it (the page's search bar), because the
     input is focused on the next animation frame. A handler bound to the panel
     swallowed nothing in that gap: Escape pressed immediately after typing
     went to the search bar and was ignored, and the palette stayed open. */
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      /* Mid-composition (a Japanese or Chinese IME, or a dead key on a
         European layout) every keystroke is the IME's, not ours. Enter there
         commits the candidate; opening a record instead loses the text and
         opens whatever happened to be selected. keyCode 229 is the older
         browsers' way of saying the same thing. */
      if (e.isComposing || e.keyCode === 229) return;
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if (e.key === "Tab") { trapTab(e, panelRef.current); return; }
      const n = resultsRef.current.length;
      if (e.key === "ArrowDown" || (e.key === "n" && e.ctrlKey)) {
        e.preventDefault();
        setSelected((i) => (n ? (i + 1) % n : 0));
        return;
      }
      if (e.key === "ArrowUp" || (e.key === "p" && e.ctrlKey)) {
        e.preventDefault();
        setSelected((i) => (n ? (i - 1 + n) % n : 0));
        return;
      }
      if (e.key === "Enter") {
        /* Enter belongs to the input. Anywhere else in the dialog it belongs
           to whatever is focused — Enter on the esc button has to close the
           palette, not open the highlighted row, which is what a blanket
           preventDefault on the window made it do. */
        if (e.target !== inputRef.current) return;
        e.preventDefault();
        openResult(resultsRef.current[selectedRef.current]);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, openResult]);

  /* Keep the selected row in view when the arrows walk past the panel edge. */
  useEffect(() => {
    const el = rowRefs.current[selected];
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "nearest" });
  }, [selected, results]);

  if (!open) return null;

  const typed = debounced.trim().length > 0;
  let flat = -1;

  return (
    <div
      style={{ ...S.overlay, ...(isMobile ? S.overlayMobile : null) }}
      data-testid="search-overlay"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        ref={panelRef}
        style={{ ...S.panel, ...(isMobile ? S.panelMobile : null) }}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        data-testid="search-palette"
      >
        <div style={S.pin}>
          <SearchIcon size={18} />
          <input
            ref={inputRef}
            style={S.pinInput}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts, clients, jobs, quotes, emails"
            aria-label="Search everything"
            aria-controls="crm-search-results"
            data-testid="search-input"
          />
          <button type="button" style={S.esc} data-testid="search-esc" onClick={() => onClose?.()}>esc</button>
        </div>

        <div ref={listRef} id="crm-search-results" role="listbox" style={S.results}>
          {groups.map((group) => (
            <div key={group.kind} style={S.group}>
              <div style={S.groupTitle}>
                <span>{group.label}</span>
                <span style={S.groupCount}>{group.results.length}</span>
              </div>
              {group.results.map((result) => {
                flat += 1;
                const at = flat;
                return (
                  <Row
                    key={`${result.kind}:${result.id}`}
                    result={result}
                    selected={at === selected}
                    isMobile={isMobile}
                    now={now}
                    rowRef={(el) => { rowRefs.current[at] = el; }}
                    onHover={() => setSelected(at)}
                    onOpen={() => openResult(result)}
                  />
                );
              })}
            </div>
          ))}
          {typed && !groups.length && (
            <div style={S.empty} data-testid="search-empty">No matches for “{debounced.trim()}”.</div>
          )}
          {!typed && (
            <div style={S.empty}>
              A name, an email, a phone, a domain, a quote handle, a word from a message.
            </div>
          )}
        </div>

        <div style={S.foot}>
          <span><Kbd>↑↓</Kbd> move</span>
          <span><Kbd>↵</Kbd> open</span>
          <span><Kbd>esc</Kbd> close</span>
          <span style={{ marginLeft: "auto" }}>Messages are searched only for admins</span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return <span style={S.kbd}>{children}</span>;
}

export function SearchIcon({ size = 16, color = C.muted }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="1.8" strokeLinecap="round" style={{ flex: "none" }} aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}

const S = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(26,26,26,0.28)",
    display: "flex", justifyContent: "center", alignItems: "flex-start",
    paddingTop: 64, zIndex: 300, fontFamily: font,
  },
  overlayMobile: { paddingTop: 0 },
  panel: {
    width: 720, maxWidth: "calc(100vw - 32px)", background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 10,
    boxShadow: "0 24px 60px rgba(26,26,26,0.18)", overflow: "hidden",
    display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 128px)",
  },
  panelMobile: {
    width: "100%", maxWidth: "100%", height: "100%", maxHeight: "100%",
    borderRadius: 0, border: "none",
  },
  pin: {
    display: "flex", alignItems: "center", gap: 12, padding: "0 16px",
    height: 52, borderBottom: `1px solid ${C.border}`, flex: "none",
  },
  pinInput: {
    flex: 1, border: 0, outline: 0, background: "transparent",
    font: `300 16px ${font}`, color: C.text, minWidth: 0,
  },
  esc: {
    fontFamily: mono, fontSize: 10, color: C.muted, background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 4, padding: "2px 6px",
  },
  results: { overflowY: "auto", flex: 1, minHeight: 0 },
  group: { padding: "6px 0 4px" },
  groupTitle: {
    fontFamily: mono, fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
    color: C.muted, padding: "6px 16px 4px", display: "flex", justifyContent: "space-between",
  },
  groupCount: { fontFamily: font, fontSize: 11, letterSpacing: 0, textTransform: "none" },
  row: {
    display: "grid", gridTemplateColumns: "22px 1fr auto", gap: 10, alignItems: "center",
    padding: "7px 16px", fontSize: 13, cursor: "pointer", color: C.text,
  },
  /* On a phone the meta line is what gets cut, and the match usually lives in
     it — so the row wraps instead of ellipsing the thing the user searched
     for. Desktop keeps the mockup's single line. */
  rowMobile: { alignItems: "start", padding: "9px 16px" },
  rowSelected: { background: C.bg },
  badge: {
    fontFamily: mono, fontSize: 10, color: C.muted, width: 22, textAlign: "center",
    border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 0", background: C.surface,
  },
  rowText: { minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  rowTextMobile: { whiteSpace: "normal", overflow: "visible", overflowWrap: "anywhere" },
  rowMeta: { color: C.muted, fontSize: 12 },
  mark: { background: "#F59E0B33", color: C.text, borderRadius: 2, padding: "0 1px" },
  rowRight: { fontFamily: mono, fontSize: 10.5, color: C.muted, whiteSpace: "nowrap" },
  empty: { padding: "22px 16px", fontSize: 12.5, color: C.muted },
  foot: {
    display: "flex", gap: 16, padding: "8px 16px", borderTop: `1px solid ${C.border}`,
    fontSize: 11, color: C.muted, background: C.bg, flex: "none", flexWrap: "wrap",
  },
  kbd: {
    fontFamily: mono, fontSize: 10, color: C.muted, border: `1px solid ${C.border}`,
    borderRadius: 4, padding: "1px 5px", background: C.surface, marginRight: 4,
  },
};
